import { reducerWithInitialState } from 'typescript-fsa-reducers';

import {
	applyAsyncReducer,
	getDefaultNetworkStatus,
	getNetworkStatusStarted,
	getNetworkStatusDone,
	getNetworkStatusFailed,
} from '@utils';

import {
	ISubscriptionsActionFetchPresentationAvailableCarouselPayloadDone,
	ISubscriptionsActionFetchPresentationOrderDetailsPayload,
	ISubscriptionsActionFetchPresentationOrderDetailsPayloadDone,
	ISubscriptionsActionSendCodePayloadDone,
	ISubscriptionsActionAvailableServisesPayload,
	ISubscriptionsActionAvailableServisesPayloadDone,
	subscriptionsFetchPresentationAvailableCarousel,
	subscriptionsPresentationAvailableCarouselSetSelectedIndex,
	subscriptionsFetchPresentationOrderDetails,
	subscriptionsSendCode,
	subscriptionsCreate,
	subscriptionsAvailableServices,
} from '@redux/actions';

import { ISubscriptionsStore } from '@interfaces';

export const getSubscriptionsInitialState = (): ISubscriptionsStore => ({
	presentationAvailableCarousel: {
		data: {
			list: [],
			selectedSlideIndex: 0,
		},
		meta: {
			networkStatus: getDefaultNetworkStatus(),
		},
	},

	presentationOrderDetails: {
		data: {
			apiId: null,
			orderDetails: null,
		},
		meta: {
			networkStatus: getDefaultNetworkStatus(),
		},
	},

	registration: {
		data: {
			msisdn: '',
			verificationCode: '',
			service: null,
			registration: null,
		},
		meta: {
			networkStatus: getDefaultNetworkStatus(),
			serviceNetworkStatus: getDefaultNetworkStatus(),
			availableServicesNetworkStatus: getDefaultNetworkStatus(),
		},
	},
});

export const subscriptions = reducerWithInitialState<ISubscriptionsStore>(getSubscriptionsInitialState())
	.case(subscriptionsPresentationAvailableCarouselSetSelectedIndex, (state, { index }) => ({
		...state,
		presentationAvailableCarousel: {
			...state.presentationAvailableCarousel,
			data: {
				...state.presentationAvailableCarousel.data,
				selectedSlideIndex: index,
			},
		},
	}));

applyAsyncReducer<
	ISubscriptionsStore['presentationAvailableCarousel']['data'],
	ISubscriptionsStore['presentationAvailableCarousel']['meta']
>()(
	'presentationAvailableCarousel',
	'networkStatus',
	subscriptionsFetchPresentationAvailableCarousel.async,
	subscriptions,
	({ store, result }: { store: ISubscriptionsStore; result: ISubscriptionsActionFetchPresentationAvailableCarouselPayloadDone }) => ({
		list: result.list,
	}),
);

applyAsyncReducer<
	ISubscriptionsStore['presentationOrderDetails']['data'],
	ISubscriptionsStore['presentationOrderDetails']['meta']
>()(
	'presentationOrderDetails',
	'networkStatus',
	subscriptionsFetchPresentationOrderDetails.async,
	subscriptions,
	({ store, params, result }: { store: ISubscriptionsStore; params: ISubscriptionsActionFetchPresentationOrderDetailsPayload; result: ISubscriptionsActionFetchPresentationOrderDetailsPayloadDone }) => ({
		apiId: params.apiId,
		orderDetails: result.orderDetails,
	}),
);

applyAsyncReducer<
	ISubscriptionsStore['registration']['data'],
	ISubscriptionsStore['registration']['meta']
>()(
	'registration',
	'serviceNetworkStatus',
	subscriptionsSendCode.async,
	subscriptions,
	({ store, result }: { store: ISubscriptionsStore; result: ISubscriptionsActionSendCodePayloadDone }) => ({
		...store.registration.data,
		service: result.service,
	}),
);

applyAsyncReducer<
	ISubscriptionsStore['registration']['data'],
	ISubscriptionsStore['registration']['meta']
>()(
	'registration',
	'availableServicesNetworkStatus',
	subscriptionsAvailableServices.async,
	subscriptions,
	({ store, params, result }: { store: ISubscriptionsStore; params: ISubscriptionsActionAvailableServisesPayload; result: ISubscriptionsActionAvailableServisesPayloadDone }) => ({
		...store.registration.data,
		msisdn: params.msisdn,
		service: result.service,
	}),
);

subscriptions
	.case(subscriptionsCreate.async.started, (state, result) => ({
		...state,
		registration: {
			...state.registration,
			meta: {
				...state.registration.meta,
				networkStatus: getNetworkStatusStarted(),
			},
		},
	}))
	.case(subscriptionsCreate.async.done, (state, result) => ({
		...state,
		registration: {
			...state.registration,
			data: {
				...state.registration.data,
				registration: result.result.registration,
				verificationCode: result.result.verificationCode,
			},
			meta: {
				...state.registration.meta,
				networkStatus: getNetworkStatusDone(),
			},
		},
	}))
	.case(subscriptionsCreate.async.failed, (state, { error, params }) => ({
		...state,
		registration: {
			...state.registration,
			data: {
				...state.registration.data,
				verificationCode: params.verificationCode,
			},
			meta: {
				...state.registration.meta,
				networkStatus: getNetworkStatusFailed(error),
			},
		},
	}));
